// components/RateUserModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiStar, FiHome, FiUser, FiArrowLeft, FiCheckCircle } from 'react-icons/fi';
import { FaChevronRight } from 'react-icons/fa';
import supabase from '../services/supabase';
import { useAuth } from '../services/auth';
import '../styles/Modals.scss';

const RateUserModal = ({ isOpen, onClose, userId, userName }) => {
  const { user } = useAuth();
  const [ratingType, setRatingType] = useState(null);
  const [selectedRating, setSelectedRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingRating, setExistingRating] = useState(null);

  const loadExistingRating = useCallback(async () => {
    if (!user) return; // signed out: no rating of theirs to find
    try {
      const { data, error } = await supabase
        .from('user_ratings')
        .select('*')
        .eq('rated_user_id', userId)
        .eq('rater_user_id', user.id)
        .eq('rating_type', ratingType)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      // Cleared when there is none: switching person or rating type used to
      // keep showing the previous one's existing rating.
      setExistingRating(data || null);
      if (data) setSelectedRating(data.rating);
    } catch (error) {
      console.error('Error loading existing rating:', error);
    }
  }, [userId, user, ratingType]);

  useEffect(() => {
    if (isOpen && ratingType && userId) {
      loadExistingRating();
    }
  }, [isOpen, ratingType, userId, loadExistingRating]);

  const handleRatingTypeSelect = (type) => {
    setRatingType(type);
    setSelectedRating(0);
  };

  const handleStarClick = (rating) => {
    setSelectedRating(rating);
  };

  const handleSubmit = async () => {
    if (selectedRating === 0) {
      alert('Please select a rating before submitting');
      return;
    }

    try {
      setIsSubmitting(true);

      if (existingRating) {
        // Update existing rating
        await supabase
          .from('user_ratings')
          .update({ rating: selectedRating })
          .eq('id', existingRating.id);
      } else {
        // Create new rating
        await supabase
          .from('user_ratings')
          .insert({
            rated_user_id: userId,
            rater_user_id: user.id,
            rating: selectedRating,
            rating_type: ratingType,
          });
      }

      alert(`Rating submitted! You rated ${userName} ${selectedRating}/10 as a ${ratingType}!`);
      handleClose();
    } catch (error) {
      console.error('Error submitting rating:', error);
      alert(error.message || 'Failed to submit rating');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setRatingType(null);
    setSelectedRating(0);
    setExistingRating(null);
    onClose();
  };

  const renderStars = () => {
    const stars = [];
    for (let i = 1; i <= 10; i++) {
      stars.push(
        <button
          key={i}
          className={`star-btn ${i <= selectedRating ? 'filled' : ''}`}
          onClick={() => handleStarClick(i)}
          type="button"
        >
          <FiStar />
        </button>
      );
    }
    return stars;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="modal-overlay" onClick={handleClose}>
          <motion.div
            className="modal-content rate-modal"
            initial={{ opacity: 0, scale: 0.9, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 50 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="modal-header">
              <h3>Rate {userName}</h3>
              <button className="close-btn" onClick={handleClose}>
                <FiX />
              </button>
            </div>

            {/* Content */}
            <div className="modal-body">
              {!ratingType ? (
                // Step 1: Select rating type
                <div className="rating-type-selection">
                  <p className="subtitle">How would you like to rate them?</p>

                  <button
                    className="type-option"
                    onClick={() => handleRatingTypeSelect('host')}
                  >
                    <div className="type-icon">
                      <FiHome />
                    </div>
                    <div className="type-info">
                      <h4>Rate as Host</h4>
                      <p>How well did they host their event?</p>
                    </div>
                    <FaChevronRight className="chevron" />
                  </button>

                  <button
                    className="type-option"
                    onClick={() => handleRatingTypeSelect('guest')}
                  >
                    <div className="type-icon">
                      <FiUser />
                    </div>
                    <div className="type-info">
                      <h4>Rate as Guest</h4>
                      <p>How were they as an attendee?</p>
                    </div>
                    <FaChevronRight className="chevron" />
                  </button>
                </div>
              ) : (
                // Step 2: Select rating
                <div className="rating-selection">
                  <button
                    className="back-btn"
                    onClick={() => setRatingType(null)}
                  >
                    <FiArrowLeft size={20} />
                    <span>Change Type</span>
                  </button>

                  <h4 className="rating-type-label">
                    Rating as {ratingType === 'host' ? 'Host' : 'Guest'}
                  </h4>

                  {existingRating && (
                    <p className="existing-rating-text">
                      You previously rated: {existingRating.rating}/10
                    </p>
                  )}

                  <p className="subtitle">Select rating out of 10</p>

                  <div className="stars-container">
                    {renderStars()}
                  </div>

                  {selectedRating > 0 && (
                    <div className="rating-display">
                      <div className="rating-value">{selectedRating}/10</div>
                      <div className="rating-percentage">
                        {Math.round((selectedRating / 10) * 100)}%
                      </div>
                    </div>
                  )}

                  <button
                    className="btn btn-primary submit-btn"
                    onClick={handleSubmit}
                    disabled={selectedRating === 0 || isSubmitting}
                  >
                    {isSubmitting ? (
                      <div className="spinner small"></div>
                    ) : (
                      <>
                        <FiCheckCircle />
                        <span>{existingRating ? 'Update Rating' : 'Submit Rating'}</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default RateUserModal;